import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { LoadingPanel } from '../../components/ui/Loader';
import { PresenceAvatar } from '../../components/ui/Presence';
import { Verified } from '../../components/ui/Verified';
import { api } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { messages } from '../messages/api';

interface Friend {
  id: number;
  username: string;
  display_name: string;
  verified: boolean;
  avatar: string | null;
  online: boolean;
}

/**
 * Inviting friends to a jam from the jam itself.
 *
 * The invite is the same message the composer's jam button sends: a
 * conversation with the person (found or started), and a message carrying
 * the jam. Doing it from here cuts the trip through Messages out. Friends
 * are mutual follows, online first, because the people you would invite
 * to listen with you right now are the ones who are here.
 */
export function InviteFriendsDialog({ jamId, onClose }: { jamId: number; onClose: () => void }) {
  const [sent, setSent] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const friends = useQuery({
    queryKey: ['me', 'friends'],
    queryFn: () => api.get<{ items: Friend[] }>('/me/friends'),
    staleTime: 60_000,
  });

  const invite = useMutation({
    mutationFn: async (username: string) => {
      const { id } = await messages.start(username);

      await messages.send(id, { jam_id: jamId });

      return username;
    },
    onSuccess: (username) => setSent((done) => [...done, username]),
    onError: () => setError('That invite did not send. Try again in a moment.'),
  });

  const needle = filter.trim().toLowerCase();
  const list = (friends.data?.items ?? []).filter(
    (f) => needle === '' || f.display_name.toLowerCase().includes(needle) || f.username.toLowerCase().includes(needle),
  );

  return (
    <Modal title="Invite friends" onClose={onClose}>
      {error && <Alert tone="error">{error}</Alert>}

      {friends.isPending ? (
        <LoadingPanel label="Finding your friends" />
      ) : (friends.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-soft" data-invite-empty>
          Nobody yet. Friends are people you follow who follow you back; once you have some, they
          show up here. You can still send the link to anybody.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {(friends.data?.items.length ?? 0) > 8 && (
            <input
              type="search"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Find a friend"
              aria-label="Find a friend"
              className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint"
              data-invite-filter
            />
          )}

          <ul className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto" data-invite-list>
            {list.map((friend) => {
              const done = sent.includes(friend.username);

              return (
                <li key={friend.id}>
                  <button
                    type="button"
                    data-invite={friend.username}
                    // Stays disabled once sent, so the third tap is known to have registered.
                    disabled={done || invite.isPending}
                    onClick={() => {
                      setError(null);
                      invite.mutate(friend.username);
                    }}
                    className="flex w-full items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5 text-left transition-colors hover:bg-raised disabled:cursor-default disabled:opacity-60"
                  >
                    {friend.online ? (
                      <PresenceAvatar src={friend.avatar} name={friend.display_name} size={32} online />
                    ) : (
                      <Avatar src={friend.avatar} name={friend.display_name} size={32} />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">
                        {friend.display_name}
                        {friend.verified && <Verified />}
                      </span>
                      <span className="block truncate text-xs text-faint">
                        @{friend.username}
                        {friend.online ? ' · online' : ''}
                      </span>
                    </span>
                    <span className="meta-caps shrink-0 text-faint">{done ? 'Invited' : 'Invite'}</span>
                  </button>
                </li>
              );
            })}
            {list.length === 0 && <li className="px-3 py-4 text-sm text-faint">Nobody by that name.</li>}
          </ul>
        </div>
      )}
    </Modal>
  );
}
